<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: meta.links.set_index,
                    },
                    {
                        name: gamificationSet.breadcrumb_title,
                        href: meta.links.set_show,
                    },
                    {
                        name: $tc('messages.itineraries', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div class="sm:col-span-4">
                    <label class="block text-sm font-medium leading-5 text-gray-700">{{ $t('messages.search') }}</label>
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <input
                            v-model="form.search"
                            type="text"
                            class="pr-10 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm w-full"
                        />
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div class="w-full flex space-x-4 justify-end">
            <AppButton :href="meta.links.create" element="inertia-link">
                {{ $t('messages.create') }}
            </AppButton>
        </div>

        <div class="font-semibold text-xl">{{ $t('messages.active_itineraries') }}</div>
        <ul role="list" class="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 xl:gap-x-8">
            <li class="relative" v-for="itinerary in itineraries.data" :key="itinerary.id">
                <div
                    class="group aspect-w-10 aspect-h-7 block w-full overflow-hidden rounded-lg bg-gray-100 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 focus-within:ring-offset-gray-100"
                >
                    <img
                        :src="itinerary.cover_image_path"
                        alt=""
                        class="pointer-events-none object-cover group-hover:opacity-75"
                    />
                    <InertiaLink :href="itinerary.links.show" class="absolute inset-0 focus:outline-none">
                        <span class="sr-only"> {{ itinerary.name }}</span>
                    </InertiaLink>
                </div>
                <ProgressBarCurrentMax
                    v-if="itinerary.stations_count"
                    :current="itinerary.avg_stations_completed"
                    :max="itinerary.stations_count"
                    color="indigo"
                    class="mt-1 w-full"
                />
                <div class="flex justify-between mt-1">
                    <p class="pointer-events-none mt-2 block truncate text-sm font-medium text-gray-900">
                        {{ itinerary.name }}
                    </p>
                    <ItineraryActionButtons :itinerary="itinerary" />
                </div>
            </li>
        </ul>

        <InertiaSimplePagination :paginator="itineraries.links" />

        <hr class="mt-5" />

        <div class="font-semibold text-xl">{{ $t('messages.inactive_itineraries') }}</div>
        <ul role="list" class="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 xl:gap-x-8">
            <li class="relative" v-for="itinerary in inactiveItineraries.data" :key="itinerary.id">
                <div
                    class="group aspect-w-10 aspect-h-7 block w-full overflow-hidden rounded-lg bg-gray-100 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 focus-within:ring-offset-gray-100"
                >
                    <img
                        :src="itinerary.cover_image_path"
                        alt=""
                        class="pointer-events-none object-cover group-hover:opacity-75"
                    />
                    <InertiaLink :href="itinerary.links.show" class="absolute inset-0 focus:outline-none">
                        <span class="sr-only"> {{ itinerary.name }}</span>
                    </InertiaLink>
                </div>
                <ProgressBarCurrentMax
                    v-if="itinerary.stations_count"
                    :current="itinerary.avg_stations_completed"
                    :max="itinerary.stations_count"
                    color="indigo"
                    class="mt-1 w-full"
                />
                <div class="flex justify-between mt-1">
                    <p class="pointer-events-none mt-2 block truncate text-sm font-medium text-gray-900">
                        {{ itinerary.name }}
                    </p>
                    <ItineraryActionButtons :itinerary="itinerary" />
                </div>
            </li>
        </ul>

        <InertiaSimplePagination :paginator="inactiveItineraries.links" />

        <hr class="mt-5" />

        <div class="font-semibold text-xl">{{ $t('messages.finished_itineraries') }}</div>
        <ul role="list" class="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 xl:gap-x-8">
            <li class="relative" v-for="itinerary in finishedItineraries.data" :key="itinerary.id">
                <div
                    class="group aspect-w-10 aspect-h-7 block w-full overflow-hidden rounded-lg bg-gray-100 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 focus-within:ring-offset-gray-100"
                >
                    <img
                        :src="itinerary.cover_image_path"
                        alt=""
                        class="pointer-events-none object-cover group-hover:opacity-75 filter saturate-0"
                    />
                    <button type="button" class="absolute inset-0 focus:outline-none">
                        <span class="sr-only">View details for IMG_4985.HEIC</span>
                    </button>
                </div>
                <div class="flex justify-between mt-1">
                    <p class="pointer-events-none mt-2 block truncate text-sm font-medium text-gray-900">
                        {{ itinerary.name }}
                    </p>
                    <ItineraryActionButtons :itinerary="itinerary" />
                </div>
            </li>
        </ul>

        <InertiaSimplePagination :paginator="finishedItineraries.links" />
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AppButton from '@/components/AppButton.vue';
import InertiaSimplePagination from '@/Shared/InertiaSimplePagination.vue';
import ProgressBarCurrentMax from '@/components/Gamification/ProgressBarCurrentMax.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import ItineraryActionButtons from '@/Pages/Teacher/Gamification/Itineraries/partials/ItineraryActionButtons.vue';

export default {
    components: {
        ItineraryActionButtons,
        SubmitFormButton,
        ProgressBarCurrentMax,
        PanelPage,
        InertiaSimplePagination,
        AppButton,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'gamificationSet', 'itineraries', 'inactiveItineraries', 'finishedItineraries', 'filters'],
    data() {
        return {
            form: this.$inertia.form({
                search: this.filters.search,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
